import { Separator, Skeleton } from '@monksflow/monks-ui';

import { formatTimeAgo } from '@/shared/utils/dateUtils';
import { OpportunityTypeBadge, DURATION_TYPE_VALUES } from '@/entities/opportunity';
import type { OpportunitiesRoomKpi } from '../../model/types';
import { OpportunityTypesInfoButton } from '../OpportunityTypesInfoButton';
import { useTranslation } from 'react-i18next';

interface Props {
  kpi: OpportunitiesRoomKpi;
  isLoading: boolean;
}

export function OpportunitiesKpiStrip({ kpi, isLoading }: Props) {
  const { t } = useTranslation('analysis');

  return (
    <div className="grid grid-cols-2 xl:grid-cols-4 gap-2">
      {/* Total */}
      <div className="col-span-1 flex flex-col justify-between rounded-lg bg-card p-section h-[120px]">
        <span className="type-heading-xs text-foreground-alt">
          {t('opportunitiesRoom.opportunitiesKpiStrip.totalOpportunities')}
        </span>
        {isLoading ? (
          <Skeleton className="h-12 w-12 self-end" />
        ) : (
          <span className="self-end type-display-md text-foreground mb-[-.5rem]">{kpi.total}</span>
        )}
      </div>

      {/* New */}
      <div className="col-span-1 flex flex-col justify-between rounded-lg bg-card p-section h-[120px]">
        <span className="type-heading-xs text-foreground-alt">
          {t('opportunitiesRoom.opportunitiesKpiStrip.newOpportunities')}
        </span>
        {isLoading ? (
          <Skeleton className="h-12 w-16 self-end" />
        ) : (
          <div className="flex flex-row gap-4 items-center justify-end">
            {kpi.lastVisitedAt && (
              <span className="type-body-sm text-muted-foreground mb-[-0.5rem]">
                {t('opportunitiesRoom.opportunitiesKpiStrip.sinceYourLastVisit', {
                  lastVisited: formatTimeAgo(kpi.lastVisitedAt),
                })}
              </span>
            )}
            <div className="flex items-center gap-1.5">
              {kpi.newCount > 0 && (
                <span className="size-2 rounded-full bg-blue-600 shrink-0" aria-hidden="true" />
              )}
              <span className="type-display-md text-foreground mb-[-0.5rem]">{kpi.newCount}</span>
            </div>
          </div>
        )}
      </div>

      {/* By duration type — spans 2 cols */}
      <div className="col-span-2 flex flex-col justify-between rounded-lg bg-card p-section max-sm:h-auto h-[120px]">
        <div className="flex items-center justify-between">
          <span className="type-heading-xs text-foreground-alt">
            {t('opportunitiesRoom.opportunitiesKpiStrip.byDurationType')}
          </span>
          <OpportunityTypesInfoButton />
        </div>
        {isLoading ? (
          <div className="flex gap-4">
            {DURATION_TYPE_VALUES.map((type) => (
              <Skeleton key={type} className="h-12 w-16 flex-1" />
            ))}
          </div>
        ) : (
          <div className="max-sm:grid max-sm:grid-cols-2 flex items-end gap-4">
            {DURATION_TYPE_VALUES.map((type, i) => {
              const count = kpi.countByType[type] ?? 0;
              return (
                <div key={type} className="contents">
                  {i > 0 && (
                    <Separator
                      orientation="vertical"
                      className="max-sm:hidden self-stretch bg-border-light"
                    />
                  )}
                  <div className="flex flex-row gap-4 flex-1 min-w-0">
                    <OpportunityTypeBadge type={type} />
                    <span className="text-xl font-medium tracking-tight text-foreground">
                      {count}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
